// src/components/WhalePortfolioTicker.jsx
import React, { useEffect, useMemo, useState } from "react";
import whalesList from "../data/whales.json";
import { fetchWhaleSnapshot } from "../services/portfolio";
import { useNavigate, Link } from "react-router-dom";

const usd = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return "$-";
  if (v >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const digits = v < 0.01 ? 8 : v < 1 ? 6 : 4;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: digits })}`;
};

const short = (a, s = 6, e = 6) =>
  !a ? "-" : a.length > s + e ? `${a.slice(0, s)}…${a.slice(-e)}` : a;

const chainToPath = (chain) => {
  const s = String(chain || "").toLowerCase();
  if (s === "ethereum" || s === "eth") return "0x1";
  if (s === "bsc" || s === "binance") return "0x38";
  if (s === "polygon" || s === "matic") return "0x89";
  if (s === "arbitrum" || s === "arb") return "0xa4b1";
  if (s === "base") return "0x2105";
  if (s === "optimism" || s === "op") return "0xa";
  if (s === "avalanche" || s === "avax") return "0xa86a";
  if (s === "fantom") return "0xfa";
  if (s === "solana" || s === "sol") return "solana";
  return s;
};

const fallbackLogo = (seed) =>
  `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(seed || "tok")}`;

export default function WhalePortfolioTicker({
  refreshMs = 180_000, // 3 min
  height = 42
}) {
  const [rows, setRows] = useState([]); // {whale, totalUsd, top:[{symbol,usdValue,address,chain}]}
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  async function load() {
    setErr(null);
    try {
      // pull all whales in parallel (gentle throttle to avoid rate limit bursts)
      const chunks = [];
      for (let i = 0; i < whalesList.length; i += 4) {
        chunks.push(whalesList.slice(i, i + 4));
      }

      const out = [];
      for (const grp of chunks) {
        const part = await Promise.allSettled(
          grp.map(async (w) => {
            const snap = await fetchWhaleSnapshot(w);
            const top = (snap.tokens || [])
              .filter(t => (t.usdValue || 0) > 0)
              .sort((a,b) => (b.usdValue || 0) - (a.usdValue || 0))
              .slice(0, 3)
              .map(t => ({
                symbol: t.symbol || "TKN",
                usdValue: t.usdValue || 0,
                address: t.address || "",
                chain: w.chain
              }));
            return {
              whale: w,
              totalUsd: snap.totalUsd || 0,
              top
            };
          })
        );
        part.forEach(p => { if (p.status === "fulfilled") out.push(p.value); });
        // tiny delay between groups to be nice to free tiers
        await new Promise(res => setTimeout(res, 450));
      }

      // sort whales by totalUsd desc
      out.sort((a, b) => (b.totalUsd || 0) - (a.totalUsd || 0));
      setRows(out);
    } catch (e) {
      setErr(e.message || String(e));
      setRows([]);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, refreshMs);
    return () => clearInterval(t);
  }, [refreshMs]);

  const feed = useMemo(() => rows.concat(rows), [rows]);

  return (
    <div className="sticky top-[56px] lg:top-[56px] bg-black text-white  text-white/90 overflow-hidden z-[8] pl-2 lg:pl-20 pr-2">
      <style>{`
        @keyframes whaleX { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
      `}</style>

      <div className="relative overflow-hidden group" style={{ height }}>
        {(!rows.length && !err) ? (
          <div className="flex items-center h-full px-3 text-[12px] text-white/60">Loading whale portfolios…</div>
        ) : err ? (
          <div className="flex items-center h-full px-3 text-[12px] text-rose-400">Whale row offline: {err}</div>
        ) : (
          <>
            <ul
              className="flex items-center gap-6 whitespace-nowrap will-change-transform group-hover:[animation-play-state:paused]"
              style={{
                width: "max-content",
                animation: "whaleX 120s linear infinite",
                animationPlayState: "running"
              }}
            >
              {feed.map((row, i) => {
                const w = row.whale;
                const chainPath = chainToPath(w.chain);
                const goToWhale = () => {
                  if (!w?.address) return;
                  navigate(`/address/${encodeURIComponent(w.address)}`);
                };

                return (
                  <li key={`${w.address}-${i}`} className="min-w-max">
                    {/* Whole chip is clickable to /address/:value */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={goToWhale}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToWhale()}
                      className="flex items-center gap-3 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer"
                      title={`Open whale • ${w.name || ""} • ${w.address || ""}`}
                    >
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/70 uppercase tracking-wide">
                        Whales
                      </span>
                      <span className="text-[12px] font-semibold">{w.name || "Unnamed"}</span>
                      {w.address ? (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-white/10 text-white/60">
                          {short(w.address, 6, 6)}
                        </span>
                      ) : null}

                      {/* separator dot */}
                      <span className="text-[12px] text-white/60 select-none">• Total</span>
                      <span className="text-[12px] font-semibold select-none">{usd(row.totalUsd)}</span>


                    </div>
                  </li>
                );
              })}
            </ul>

          </>
        )}
      </div>
    </div>
  );
}
